import moment from "moment";
import React from "react";
import { AiOutlineShop } from "react-icons/ai";
import { FaFileInvoice } from "react-icons/fa";
import { FcClock } from "react-icons/fc";
import { FiPhone, FiUser } from "react-icons/fi";
import {
  HiBadgeCheck,
  HiOutlineLocationMarker,
  HiOutlineMail,
} from "react-icons/hi";
import { IoCardOutline } from "react-icons/io5";
import { MdOutlineShoppingCart } from "react-icons/md";
import { RiCloseCircleFill } from "react-icons/ri";
import { Box, BoxHeading, BoxText, BoxTitle } from "../../../components/Box";
import { CreateAndDetailLayout } from "../../../components/CreateAndDetailLayout";
import { InfoCards } from "../../../components/InfoCards";
import { OrderConfirmed } from "../../../components/OrderConfirmed";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { TableContent } from "../../../components/panel/table/TableContent";
import { NOIMAGE_LINK } from "../../../global/variables";
import { useOneOrderConfirme } from "../../../hook/queries/useOrdersCofirmes";
import { OrderItem } from "../../../hook/queries/useOrdersV2";
import { Container } from "./styles";

interface Props {
  match: {
    params: {
      id: string;
    };
  };

  admin: boolean;
  client: boolean;
  seller: boolean;
  is_manager?: boolean;
  is_supervisor?: boolean;
}

export function OrderConfirmDetail({ match, client }: Props) {
  const getOrderConfirm = useOneOrderConfirme(match.params.id);
  const order = getOrderConfirm?.data?.order;

  return (
    <CreateAndDetailLayout isLoading={getOrderConfirm?.isLoading}>
      <PanelAndDetailAndCreateHeader title={`Pedido ${order?.id}`} goBack />

      <InfoCards
        style={{
          margin: "0",
        }}
        cards={[
          {
            title: "Código",
            value: order?.code.toString() ?? "-",
          },
          {
            title: "Criado em",
            value: moment(order?.entryDate).format("DD/MM/YYYY"),
          },
          {
            title: "Confirmado",
            value:
              getOrderConfirm?.data?.confirmed === null ? (
                <FcClock color="#417DAB" size={26} />
              ) : getOrderConfirm?.data?.confirmed ? (
                <HiBadgeCheck color="#21b543" size={26} />
              ) : (
                <RiCloseCircleFill color="#d21e26" size={26} />
              ),
          },
          ...(getOrderConfirm?.data?.confirmedAt
            ? [
                {
                  title: "Confirmado em",
                  value: moment(getOrderConfirm?.data?.confirmedAt).format(
                    "DD/MM/YYYY HH:mm:ss"
                  ),
                },
              ]
            : []),
        ]}
      />

      {getOrderConfirm?.data?.confirmed === null && client && (
        <OrderConfirmed
          orderId={getOrderConfirm?.data.orderId}
          onFinally={() => getOrderConfirm.refetch()}
        />
      )}

      <Container>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            gridArea: "ORDERMAIN",
          }}
        >
          <Box>
            <BoxHeading>Itens do Pedido</BoxHeading>

            <TableContent<OrderItem & { image: string }>
              textAlign="start"
              columns={[
                {
                  field: "Imagem",
                  name: "image",
                  viewList: true,
                  tdStyles: () => ({ padding: "0.5rem" }),
                  render: (item) => (
                    <img
                      src={item.image}
                      alt="Produto"
                      style={{
                        width: "8rem",
                        height: "8rem",
                        objectFit: "contain",
                        borderRadius: "0.5rem",
                      }}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null; // prevents looping
                        currentTarget.src = NOIMAGE_LINK;
                      }}
                    />
                  ),
                },
                {
                  field: "Produto",
                  name: "product",
                  viewList: true,
                  render: (item) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                      }}
                    >
                      <strong>{item.product.description}</strong>
                      <span>Referência: {item.product.reference}</span>
                      <span>Grade: {item.product.grid}</span>
                      <span>Origem: {item.product.origin}</span>
                    </div>
                  ),
                },

                { field: "Qtd", name: "quantity", viewList: true },
                { field: "Valor Unit.", name: "unitValue", viewList: true },
                { field: "Desconto", name: "discountValue", viewList: true },
                { field: "Total", name: "totalValue", viewList: true },
              ]}
              data={order?.items?.map((item) => ({
                ...item,
                image: `https://alpar2.sfo3.digitaloceanspaces.com/Produtos/${item.product.reference}_01_smaller`,
                product: item.product,
                status: item.status,
                quantity: item.quantity,
                netValue: Number(item.netValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
                totalValue: Number(item.netValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
                discountValue: Number(item.discountValue ?? 0).toLocaleString(
                  "pt-BR",
                  {
                    style: "currency",
                    currency: "BRL",
                  }
                ),
                unitValue: Number(
                  Number(item.netValue) / item.quantity
                ).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
              }))}
            />

            <div style={{ textAlign: "end", marginTop: "1rem" }}>
              <BoxText>
                Subtotal:{" "}
                {Number(order?.totalMerchandise).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </BoxText>

              <BoxHeading style={{ justifyContent: "end" }}>
                Total:{" "}
                {Number(order?.totalInvoiceValue).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </BoxHeading>
            </div>
          </Box>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            gridArea: "ORDERDETAILS",
            width: "100%",
          }}
        >
          {order?.invoiceNumber && (
            <Box>
              <BoxHeading icon={FaFileInvoice}>Numero nota fiscal</BoxHeading>

              <BoxTitle>NF {order?.invoiceNumber}</BoxTitle>
            </Box>
          )}

          <Box>
            <BoxHeading icon={MdOutlineShoppingCart}>Detalhes</BoxHeading>

            <BoxTitle>{order?.brand?.name}</BoxTitle>
            <BoxText style={{ marginBottom: "0.5rem" }}>
              {order?.species.name}
            </BoxText>

            <BoxTitle>Condição pagamento</BoxTitle>
            <BoxText icon={IoCardOutline}>{order?.paymentMethod}</BoxText>
          </Box>

          {!!order?.client && (
            <Box>
              <BoxHeading icon={AiOutlineShop}>Cliente</BoxHeading>
              {!client && (
                <BoxText>{order?.client?.concept?.description}</BoxText>
              )}

              <BoxTitle>
                {order?.client?.trade_name} ({order?.client?.cod})
              </BoxTitle>
              <BoxText style={{ marginBottom: "0.5rem" }}>
                {order?.client?.cnpj}
              </BoxText>

              <BoxText icon={HiOutlineMail}>{order?.client?.email}</BoxText>
              <BoxText icon={FiPhone}>{order?.client?.cellPhone}</BoxText>

              {!!order?.client?.adresses && (
                <BoxText icon={HiOutlineLocationMarker}>
                  {order?.client?.adresses?.street},{" "}
                  {order?.client?.adresses?.numberStreet} -{" "}
                  {order?.client?.adresses?.neighborhood},{" "}
                  {order?.client?.adresses?.city} -{" "}
                  {order?.client?.adresses?.uf},{" "}
                  {order?.client?.adresses?.zip_code}
                </BoxText>
              )}
            </Box>
          )}

          {!!order?.seller && (
            <Box>
              <BoxHeading icon={FiUser}>Representante</BoxHeading>

              <BoxTitle>{order?.seller?.fullName} </BoxTitle>
              <BoxText style={{ marginBottom: "0.5rem" }}>
                {order?.seller?.cod} - {order?.seller?.abbreviation}
              </BoxText>

              <BoxText icon={HiOutlineMail}>{order?.seller?.email}</BoxText>
              <BoxText icon={FiPhone}>{order?.seller?.phone}</BoxText>
            </Box>
          )}
        </div>
      </Container>
    </CreateAndDetailLayout>
  );
}
